import type {
  LeDataChatMessage,
  LeDataContentBlock,
  LeDataContentType,
  LeDataEngine,
  LeDataFeedbackRating,
  LeDataImageAttachment,
  LeDataImageMediaType,
  LeDataProvenance,
  LeDataSessionAccess,
  LeDataSessionMetadata,
  LeDataSessionResponse,
  LeDataSessionVisibility,
  LeDataUsageSummary,
} from '~/types/leData';

import { asMantleMode } from '~/utils/leData/engine';
import { parseLeDataFeedbackRating, parseLeDataProvenance } from '~/utils/leData/provenance';

const CONTENT_TYPES = new Set<string>(['text', 'sql', 'table', 'chart', 'file', 'error']);

function inferContentType(entry: Record<string, unknown>): LeDataContentType | null {
  const explicitType = typeof entry.type === 'string' ? entry.type.toLowerCase() : '';
  if (CONTENT_TYPES.has(explicitType)) {
    return explicitType as LeDataContentType;
  }

  if (typeof entry.chartType === 'string' && Array.isArray(entry.data)) {
    return 'chart';
  }

  if (Array.isArray(entry.columns) && Array.isArray(entry.rows)) {
    return 'table';
  }

  if (typeof entry.sql === 'string' || typeof entry.query === 'string' || typeof entry.statement === 'string') {
    return 'sql';
  }

  if (typeof entry.url === 'string' || typeof entry.href === 'string' || typeof entry.downloadUrl === 'string') {
    return 'file';
  }

  if (typeof entry.text === 'string' || typeof entry.markdown === 'string' || typeof entry.content === 'string') {
    return 'text';
  }

  if (typeof entry.message === 'string' && entry.message.trim()) {
    return 'text';
  }

  if (entry.is_error === true || typeof entry.error === 'string' || typeof entry.detail === 'string') {
    return 'error';
  }

  return null;
}

function newBlockId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `blk-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function newMessageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function createThoughtTextPayload(text: string, entry?: Record<string, unknown>): Record<string, unknown> {
  const payload =
    entry && typeof entry.payload === 'object' && entry.payload !== null && !Array.isArray(entry.payload)
      ? { ...entry, ...(entry.payload as Record<string, unknown>) }
      : { ...(entry ?? {}) };

  const markdown =
    typeof payload.markdown === 'string'
      ? payload.markdown
      : typeof payload.text === 'string'
        ? payload.text
        : typeof payload.content === 'string'
          ? payload.content
          : text;

  return {
    ...payload,
    markdown,
    section: 'thought-process',
  };
}

function asBlocks(raw: unknown): Array<LeDataContentBlock> | null {
  if (!Array.isArray(raw)) {
    return null;
  }
  const out: Array<LeDataContentBlock> = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const o = item as Record<string, unknown>;
    const type = inferContentType(o) ?? 'text';
    const payload =
      typeof o.payload === 'object' && o.payload !== null && !Array.isArray(o.payload)
        ? (o.payload as Record<string, unknown>)
        : o;
    out.push({
      id: typeof o.id === 'string' ? o.id : newBlockId(),
      type,
      payload,
    });
  }
  return out.length > 0 ? out : null;
}

function safeJsonParse(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function extractTextFromBlocks(blocks: Array<LeDataContentBlock>): string | undefined {
  const parts = blocks
    .filter((block) => block.type === 'text')
    .map((block) => {
      const { payload } = block;
      return [payload.markdown, payload.text, payload.content, payload.message].find(
        (value): value is string => typeof value === 'string' && value.trim().length > 0,
      );
    })
    .filter((value): value is string => Boolean(value));

  if (parts.length === 0) {
    return undefined;
  }

  return parts.join('\n\n');
}

function extractNonTextBlocks(blocks: Array<LeDataContentBlock>): Array<LeDataContentBlock> {
  return blocks.filter((block) => block.type !== 'text');
}

function asUserFacingToolResultBlocks(entry: Record<string, unknown>): Array<LeDataContentBlock> {
  if (entry.is_error === true && typeof entry.content === 'string' && entry.content.trim()) {
    return [{ id: newBlockId(), type: 'error', payload: { message: entry.content.trim() } }];
  }

  if (typeof entry.content !== 'string' || !entry.content.trim()) {
    return [];
  }

  const parsed = safeJsonParse(entry.content);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return [];
  }

  const root = parsed as Record<string, unknown>;
  if (root.kind === 'lens_draft' && typeof root.lensId === 'string') {
    return [{ id: newBlockId(), type: 'lens_draft', payload: root }];
  }
  // `scope_lens`/`draft_lens` results (03_CONVERSATIONAL_BUILD_PLAN.md): a live SSE lens_plan
  // block carries the full `plan`, but this same object persisted here (and the model-facing
  // draft_lens result) never does, so a reload's Build always goes through the refetch-by-planId
  // path (LensPlanBlock's own `needsPlanFetch`), never a stale/partial `plan`.
  if (root.kind === 'lens_scope' && typeof root.brief === 'string') {
    return [{ id: newBlockId(), type: 'lens_scope', payload: root }];
  }
  if (root.kind === 'lens_plan' && typeof root.planId === 'string') {
    return [{ id: newBlockId(), type: 'lens_plan', payload: root }];
  }
  if (root.kind === 'lens_created' && typeof root.lensId === 'string') {
    return [{ id: newBlockId(), type: 'lens_created', payload: root }];
  }
  const meta =
    root.meta && typeof root.meta === 'object' && !Array.isArray(root.meta)
      ? (root.meta as Record<string, unknown>)
      : null;
  const data =
    root.data && typeof root.data === 'object' && !Array.isArray(root.data)
      ? (root.data as Record<string, unknown>)
      : root;

  const tool = typeof meta?.tool === 'string' ? meta.tool : typeof meta?.route === 'string' ? meta.route : '';
  if (tool !== 'run_sql') {
    return [];
  }

  const blocks: Array<LeDataContentBlock> = [];

  if (typeof data.sql === 'string' && data.sql.trim()) {
    blocks.push({
      id: newBlockId(),
      type: 'sql',
      payload: {
        content: data.sql,
      },
    });
  }

  if (Array.isArray(data.columns) && Array.isArray(data.rows)) {
    blocks.push({
      id: newBlockId(),
      type: 'table',
      payload: {
        columns: data.columns,
        rows: data.rows,
      },
    });
  }

  if (typeof data.chartType === 'string' && Array.isArray(data.data)) {
    blocks.push({
      id: newBlockId(),
      type: 'chart',
      payload: data,
    });
  }

  return blocks;
}

function asSessionContentBlocks(raw: unknown): Array<LeDataContentBlock> | null {
  if (!Array.isArray(raw)) {
    return null;
  }

  const blocks: Array<LeDataContentBlock> = [];
  const hasToolActivity = raw.some((item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      return false;
    }
    const typeValue = (item as Record<string, unknown>).type;
    const typeRaw = typeof typeValue === 'string' ? typeValue.toLowerCase() : '';
    return typeRaw === 'tool_use' || typeRaw === 'tool_result';
  });

  for (const item of raw) {
    if (typeof item === 'string' && item.trim()) {
      blocks.push({
        id: newBlockId(),
        type: 'text',
        payload: hasToolActivity ? createThoughtTextPayload(item) : { markdown: item },
      });
      continue;
    }

    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      continue;
    }

    const entry = item as Record<string, unknown>;
    const typeRaw = typeof entry.type === 'string' ? entry.type.toLowerCase() : '';
    const inferredType = inferContentType(entry);

    if (typeRaw === 'tool_use') {
      if (entry.name === 'run_sql') {
        const input = entry.input as Record<string, unknown> | undefined;
        const sql = typeof input?.sql === 'string' ? input.sql.trim() : '';
        if (sql) {
          blocks.push({
            id: typeof entry.id === 'string' ? entry.id : newBlockId(),
            type: 'sql',
            payload: { content: sql, dialect: 'bigquery' },
          });
        }
      }
      continue;
    }

    if (typeRaw === 'tool_result') {
      blocks.push(...asUserFacingToolResultBlocks(entry));
      continue;
    }

    if (inferredType) {
      if (hasToolActivity && inferredType === 'text') {
        const text =
          typeof entry.markdown === 'string'
            ? entry.markdown
            : typeof entry.text === 'string'
              ? entry.text
              : typeof entry.content === 'string'
                ? entry.content
                : typeof entry.message === 'string'
                  ? entry.message
                  : '';
        if (text.trim()) {
          blocks.push({
            id: typeof entry.id === 'string' ? entry.id : newBlockId(),
            type: 'text',
            payload: createThoughtTextPayload(text, entry),
          });
        }
        continue;
      }
      const payload =
        typeof entry.payload === 'object' && entry.payload !== null && !Array.isArray(entry.payload)
          ? { ...entry, ...(entry.payload as Record<string, unknown>) }
          : entry;
      blocks.push({
        id: typeof entry.id === 'string' ? entry.id : newBlockId(),
        type: inferredType,
        payload,
      });
      continue;
    }

    if (!hasToolActivity && typeof entry.text === 'string' && entry.text.trim()) {
      blocks.push({
        id: typeof entry.id === 'string' ? entry.id : newBlockId(),
        type: 'text',
        payload: { markdown: entry.text },
      });
    }
  }

  return blocks.length > 0 ? blocks : null;
}

/**
 * Coerces a persisted usage object into a summary for session replay.
 *
 * Lenient by design so partial/edge usage still renders when reopening an old chat, matching the
 * live SSE path (`ReportingAiAskService`), which trusts the server object without validation. The
 * backend's `summarizeUsage` can legitimately emit a usage object with a missing `toolCalls`
 * (no MCP calls recorded) or a non-numeric `costUsd` (cost estimate for an empty/unknown model id);
 * treating either as fatal used to drop the whole stats line on reload. Each numeric field falls
 * back to 0, and we bail out only when `raw` is not a usable object or carries no usage signal at
 * all (so we never render an all-zero line for sessions that genuinely have no usage).
 */
function asUsageSummary(raw: unknown): LeDataUsageSummary | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }
  const usage = raw as Record<string, unknown>;
  const num = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

  const cacheRead = num(usage.cacheRead);
  const cacheWrite = num(usage.cacheWrite);
  const input = num(usage.input);
  const output = num(usage.output);
  const toolCalls = num(usage.toolCalls);
  const costUsd = num(usage.costUsd);
  const rawTotal = num(usage.total);
  const total = rawTotal > 0 ? rawTotal : cacheRead + cacheWrite + input + output;

  if (total <= 0 && input === 0 && output === 0 && cacheRead === 0 && cacheWrite === 0 && toolCalls === 0) {
    return undefined;
  }

  return {
    total,
    cacheRead,
    cacheWrite,
    input,
    output,
    toolCalls,
    model: typeof usage.model === 'string' ? usage.model : '',
    costUsd,
  };
}

function asSessionVisibility(raw: unknown): LeDataSessionVisibility | undefined {
  return raw === 'private' || raw === 'luxuryescapes_link' ? raw : undefined;
}

type AssistantAnswerMeta = {
  traceId?: string;
  provenance?: LeDataProvenance;
  feedbackRating?: LeDataFeedbackRating;
};

/**
 * svc-reporting persists provenance at SESSION level: `session.metadata.provenance` is an array of
 * `{ messageIndex, traceId, confidence, tables, terms }`, where `messageIndex` is the index of the
 * run's final assistant row in the session's raw messages array (agent.ts recordAndEmitProvenance).
 * Session message rows carry no metadata of their own, so this join is the primary replay path;
 * the per-row reads in `extractAssistantAnswerMeta` remain as a forward-compatible fallback.
 * Later entries win on a duplicate index.
 */
function parseSessionProvenanceIndex(raw: unknown): Map<number, LeDataProvenance> {
  const byIndex = new Map<number, LeDataProvenance>();
  if (!Array.isArray(raw)) {
    return byIndex;
  }
  for (const item of raw) {
    const entry = asRecord(item);
    if (!entry) {
      continue;
    }
    const rawIndex = entry.messageIndex ?? entry.message_index;
    if (typeof rawIndex !== 'number' || !Number.isInteger(rawIndex) || rawIndex < 0) {
      continue;
    }
    const provenance = parseLeDataProvenance(entry);
    if (provenance) {
      byIndex.set(rawIndex, provenance);
    }
  }
  return byIndex;
}

/**
 * Session-level `metadata.messageTimes`: `{ messageIndex, createdAt }` parallel to provenance.
 * Mantle builds these from turn startedAt/finishedAt; invoke records them when messages are pushed.
 * Later entries win on a duplicate index.
 */
function parseSessionMessageTimesIndex(raw: unknown): Map<number, string> {
  const byIndex = new Map<number, string>();
  if (!Array.isArray(raw)) {
    return byIndex;
  }
  for (const item of raw) {
    const entry = asRecord(item);
    if (!entry) {
      continue;
    }
    const rawIndex = entry.messageIndex ?? entry.message_index;
    const createdAt = entry.createdAt ?? entry.created_at;
    if (typeof rawIndex !== 'number' || !Number.isInteger(rawIndex) || rawIndex < 0) {
      continue;
    }
    if (typeof createdAt !== 'string' || !createdAt.trim()) {
      continue;
    }
    byIndex.set(rawIndex, createdAt);
  }
  return byIndex;
}

function createdAtFromIndex(byIndex: Map<number, string>, rawIndex: number): { createdAt?: string } {
  const createdAt = byIndex.get(rawIndex);
  return createdAt ? { createdAt } : {};
}

/**
 * Extracts provenance / traceId / feedback persisted by svc-reporting on an assistant session row.
 * Must stay in sync with the live SSE dispatch in `ReportingAiAskService` (both use `parseLeDataProvenance`).
 */
function extractAssistantAnswerMeta(row: Record<string, unknown>): AssistantAnswerMeta {
  const metadata =
    row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata)
      ? (row.metadata as Record<string, unknown>)
      : undefined;

  const provenance =
    parseLeDataProvenance(row.provenance) ??
    parseLeDataProvenance(metadata?.provenance) ??
    parseLeDataProvenance(metadata);

  const traceId = [row.traceId, row.trace_id, metadata?.traceId, metadata?.trace_id, provenance?.traceId].find(
    (value): value is string => typeof value === 'string' && value.trim().length > 0,
  );

  const feedbackRating = parseLeDataFeedbackRating(
    row.feedback ?? metadata?.feedback ?? row.feedbackRating ?? metadata?.feedbackRating,
  );

  const meta: AssistantAnswerMeta = {};
  if (traceId) {
    meta.traceId = traceId;
  }
  if (provenance) {
    meta.provenance = provenance;
  }
  if (feedbackRating) {
    meta.feedbackRating = feedbackRating;
  }
  return meta;
}

function asRecord(raw: unknown): Record<string, unknown> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }
  return raw as Record<string, unknown>;
}

function asSessionMetadata(raw: unknown): LeDataSessionMetadata | undefined {
  const session = asRecord(raw);
  if (!session) {
    return undefined;
  }

  const sessionId =
    typeof session.sessionId === 'string'
      ? session.sessionId
      : typeof session.session_id === 'string'
        ? session.session_id
        : typeof session.id === 'string'
          ? session.id
          : '';
  if (!sessionId) {
    return undefined;
  }

  const visibility =
    asSessionVisibility(session.visibility) ??
    asSessionVisibility(session.visibilityStatus) ??
    asSessionVisibility(session.sharedVisibility);

  const metadata: LeDataSessionMetadata = { sessionId };
  const nestedMetadata = asRecord(session.metadata);
  // Background Lens runs are retained on the service for auditability, but
  // are deliberately not person-started chats. Preserve the stamp when a
  // session is opened directly (for example in the playground). Some service
  // versions put that immutable birth stamp under `session.metadata`, so read
  // both response shapes before falling back to the prompt classifier.
  const isFalseLike = (value: unknown): boolean => value === false || value === 'false' || value === 0;
  if (
    isFalseLike(session.humanInitiated) ||
    isFalseLike(session.human_initiated) ||
    isFalseLike(nestedMetadata?.humanInitiated) ||
    isFalseLike(nestedMetadata?.human_initiated)
  ) {
    metadata.humanInitiated = false;
  } else if (
    session.humanInitiated === true ||
    session.human_initiated === true ||
    nestedMetadata?.humanInitiated === true ||
    nestedMetadata?.human_initiated === true
  ) {
    metadata.humanInitiated = true;
  }
  if (visibility) {
    metadata.visibility = visibility;
  }
  // Tolerate both shapes the server uses for the pinned mode: directly on the session
  // (`session.mode`) or nested alongside the engine stamp (`session.metadata.mode`).
  const mode = asMantleMode(session.mode) ?? asMantleMode(nestedMetadata?.mode);
  if (mode) {
    metadata.mode = mode;
  }
  if (typeof session.sharedAt === 'string') {
    metadata.sharedAt = session.sharedAt;
  } else if (typeof session.shared_at === 'string') {
    metadata.sharedAt = session.shared_at;
  } else if (session.sharedAt === null) {
    metadata.sharedAt = null;
  }

  return metadata;
}

function asSessionAccess(raw: unknown): LeDataSessionAccess | undefined {
  const access = asRecord(raw);
  if (
    access &&
    (access.mode === 'shared' || access.mode === 'owner') &&
    typeof access.canEdit === 'boolean' &&
    access.visibility === 'luxuryescapes_link'
  ) {
    return {
      mode: access.mode,
      canEdit: access.canEdit,
      visibility: 'luxuryescapes_link',
    };
  }

  return undefined;
}

/* ---------------------- OpenAI Responses (mantle) session replay ---------------------- */
/*
 * Mantle history is Responses items (message / reasoning / function_call*), not Anthropic.
 * Walk rebuilds chat turns:
 *
 *  - a user message row starts a new turn;
 *  - tool-output rows (`function_call_result`, the persisted @openai/agents type, or the raw
 *    `function_call_output`) replay the run's working-out through the same run_sql result parser
 *    the Anthropic path uses (SQL + result table + chart);
 *  - assistant message rows are narration (thought-process blocks) when a tool call follows
 *    them later in the turn, and the final answer otherwise - the exact classification the
 *    engine itself applies while streaming;
 *  - `reasoning` items (encrypted) and `function_call` argument rows render nothing.
 */

/** True for a Responses chat message row with the given role (string or array content). */
function isResponsesMessageRow(row: Record<string, unknown>, role: 'user' | 'assistant'): boolean {
  return row.role === role && (typeof row.content === 'string' || Array.isArray(row.content));
}

/** Joined text of a Responses assistant message's `output_text` parts (or its plain string content). */
function extractResponsesOutputText(row: Record<string, unknown>): string {
  if (typeof row.content === 'string') {
    return row.content.trim();
  }
  if (!Array.isArray(row.content)) {
    return '';
  }
  return row.content
    .map((part) => {
      const p = asRecord(part);
      if (p && (p.type === 'output_text' || p.type === 'text') && typeof p.text === 'string') {
        return p.text;
      }
      // A completed Responses message can be a refusal instead of output_text; surface it so a
      // chat that ended in a policy/safety refusal still shows the assistant's response on replay.
      if (p?.type === 'refusal' && typeof p.refusal === 'string') {
        return p.refusal;
      }
      return '';
    })
    .filter(Boolean)
    .join('\n\n')
    .trim();
}

const DATA_URL_IMAGE_PATTERN = /^data:(image\/(?:png|jpeg|gif|webp));base64,(.+)$/;

/** Responses user message row → chat row, recovering text and any input_image attachments. */
function mapResponsesUserMessage(row: Record<string, unknown>, id: string): LeDataChatMessage | null {
  if (typeof row.content === 'string') {
    return row.content.trim() ? { id, role: 'user', text: row.content } : null;
  }
  if (!Array.isArray(row.content)) {
    return null;
  }

  const texts: Array<string> = [];
  const images: Array<LeDataImageAttachment> = [];
  for (const part of row.content) {
    const p = asRecord(part);
    if (!p) {
      continue;
    }
    if ((p.type === 'input_text' || p.type === 'text') && typeof p.text === 'string' && p.text.trim()) {
      texts.push(p.text);
    } else if (p.type === 'input_image' && typeof p.image === 'string') {
      const match = p.image.match(DATA_URL_IMAGE_PATTERN);
      if (match) {
        images.push({ mediaType: match[1] as LeDataImageMediaType, data: match[2] });
      }
    }
  }

  const text = texts.join('\n\n').trim();
  if (!text && images.length === 0) {
    return null;
  }
  return {
    id,
    role: 'user',
    text: text || undefined,
    images: images.length > 0 ? images : undefined,
  };
}

/** The SQL string of a `run_sql` function_call item — its query lives in the call arguments,
 *  not the tool result — or null for any other call. */
function extractRunSqlFromCall(row: Record<string, unknown>): string | null {
  if (row.name !== 'run_sql' || typeof row.arguments !== 'string') {
    return null;
  }
  const parsed = safeJsonParse(row.arguments);
  const sql =
    parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>).sql : null;
  return typeof sql === 'string' && sql.trim() ? sql : null;
}

/** The string payload of a Responses `function_call_output` row, across SDK shape variants. */
function extractResponsesToolOutputText(row: Record<string, unknown>): string {
  const output = row.output;
  if (typeof output === 'string') {
    return output;
  }
  const record = asRecord(output);
  if (record && typeof record.text === 'string') {
    return record.text;
  }
  if (Array.isArray(output)) {
    return output
      .map((part) => {
        const p = asRecord(part);
        return p && typeof p.text === 'string' ? p.text : '';
      })
      .filter(Boolean)
      .join('\n');
  }
  return '';
}

/**
 * Rebuilds chat messages from a mantle session's Responses items. One assistant chat message
 * per turn, carrying (in stream order) the narration thought blocks, the run_sql working-out
 * (SQL / table / chart), and the final answer prose. Session-level provenance entries are
 * joined onto the turn containing their raw messageIndex (the reshow endpoint anchors each turn's
 * provenance to the index of that turn's last visible item; later entries win).
 *
 * This is THE mantle reshow path: the server returns the model-context ledger with reasoning
 * stripped, and this walk projects it to chat rows exactly as the live stream renders them.
 */
function mapResponsesItemsToMessages(
  rawMessages: Array<unknown>,
  provenanceByIndex: Map<number, LeDataProvenance>,
  messageTimesByIndex: Map<number, string>,
  usage: LeDataUsageSummary | undefined,
): Array<LeDataChatMessage> {
  const result: Array<LeDataChatMessage> = [];
  let turnRows: Array<{ row: Record<string, unknown>; rawIndex: number }> = [];

  const flushTurn = (): void => {
    if (turnRows.length === 0) {
      return;
    }
    const rows = turnRows;
    turnRows = [];

    // An assistant message is narration when a tool call follows it later in the turn.
    // `scope_lens`/`draft_lens` calls do not count: `runLensIntent` persists a lensIntent turn as
    // a synthetic call/result pair for reshow, but it never ran the agent, so the assistant
    // sentence in front of it ("Quick check before this Lens is built.") is the actual answer, not
    // the agent's own working-out, and must not be demoted to a thought-process block.
    let lastCallIndex = -1;
    for (let i = 0; i < rows.length; i += 1) {
      const { row } = rows[i];
      if (row.type === 'function_call' && row.name !== 'scope_lens' && row.name !== 'draft_lens') {
        lastCallIndex = i;
      }
    }

    const blocks: Array<LeDataContentBlock> = [];
    let answerRowId: string | null = null;
    for (let i = 0; i < rows.length; i += 1) {
      const { row } = rows[i];
      const type = typeof row.type === 'string' ? row.type : '';
      if (type === 'reasoning') {
        continue;
      }
      if (type === 'function_call') {
        // run_sql carries its query in the CALL arguments; the tool result holds only
        // columns/rows. Emit the SQL here so it renders before that result's table, as live did.
        const sql = extractRunSqlFromCall(row);
        if (sql) {
          blocks.push({ id: newBlockId(), type: 'sql', payload: { content: sql } });
        }
        continue;
      }
      // `function_call_result` is the persisted @openai/agents item type; `function_call_output`
      // is the raw Responses API name. Handle both — a missed one drops the run's working-out
      // (SQL / result table / chart) from the reshown answer.
      if (type === 'function_call_output' || type === 'function_call_result') {
        const content = extractResponsesToolOutputText(row);
        if (content) {
          blocks.push(...asUserFacingToolResultBlocks({ content }));
        }
        continue;
      }
      if (isResponsesMessageRow(row, 'assistant')) {
        const text = extractResponsesOutputText(row);
        if (!text) {
          continue;
        }
        if (i < lastCallIndex) {
          blocks.push({ id: newBlockId(), type: 'text', payload: createThoughtTextPayload(text) });
        } else {
          blocks.push({ id: newBlockId(), type: 'text', payload: { markdown: text } });
          answerRowId = answerRowId ?? (typeof row.id === 'string' && row.id ? row.id : null);
        }
      }
    }
    if (blocks.length === 0) {
      return;
    }

    // Latest provenance entry recorded against any raw row of this turn wins.
    const firstRawIndex = rows[0].rawIndex;
    const lastRawIndex = rows[rows.length - 1].rawIndex;
    let provenance: LeDataProvenance | undefined;
    let provenanceIndex = -1;
    provenanceByIndex.forEach((entry, index) => {
      if (index >= firstRawIndex && index <= lastRawIndex && index > provenanceIndex) {
        provenance = entry;
        provenanceIndex = index;
      }
    });

    result.push({
      id: answerRowId ?? newMessageId(),
      role: 'assistant',
      progress: null,
      blocks,
      ...createdAtFromIndex(messageTimesByIndex, lastRawIndex),
      ...(provenance ? { provenance } : {}),
      ...(provenance?.traceId ? { traceId: provenance.traceId } : {}),
    });
  };

  for (let rawIndex = 0; rawIndex < rawMessages.length; rawIndex += 1) {
    const row = asRecord(rawMessages[rawIndex]);
    if (!row) {
      continue;
    }
    if (isResponsesMessageRow(row, 'user')) {
      flushTurn();
      const id = typeof row.id === 'string' && row.id ? row.id : newMessageId();
      const message = mapResponsesUserMessage(row, id);
      if (message) {
        result.push({ ...message, ...createdAtFromIndex(messageTimesByIndex, rawIndex) });
      }
      continue;
    }
    turnRows.push({ row, rawIndex });
  }
  flushTurn();

  if (usage) {
    for (let i = result.length - 1; i >= 0; i -= 1) {
      if (result[i].role === 'assistant') {
        result[i] = { ...result[i], usage };
        break;
      }
    }
  }

  return result;
}

/**
 * Digs the raw server-side message rows out of the session response, tolerating the same
 * shape variants `mapLeDataSessionResponseToMessages` accepts (`messages`, `items`, nested
 * `data.session`, a bare array). Shared with the engine detection in
 * `parseLeDataSessionResponse` so both always look at the same rows.
 */
function extractRawSessionMessages(body: unknown): Array<unknown> | null {
  if (!body || typeof body !== 'object') {
    return null;
  }
  const root = body as Record<string, unknown>;
  const dataObj = asRecord(root.data);
  const nestedSession = asRecord(dataObj?.session) ?? asRecord(root.session);
  const sessionObj = nestedSession ?? dataObj ?? root;
  const rawMessages =
    sessionObj.messages ??
    sessionObj.items ??
    dataObj?.messages ??
    dataObj?.items ??
    root.messages ??
    root.items ??
    (Array.isArray(body) ? body : null);
  return Array.isArray(rawMessages) ? rawMessages : null;
}

/**
 * Maps GET /api/reporting/le-data/sessions/:sessionId JSON into chat rows.
 * Accepts several likely shapes (`messages`, `items`, nested `data.session`, etc.).
 */
export function mapLeDataSessionResponseToMessages(body: unknown): Array<LeDataChatMessage> {
  if (!body || typeof body !== 'object') {
    return [];
  }
  const root = body as Record<string, unknown>;
  const dataObj = asRecord(root.data);
  const nestedSession = asRecord(dataObj?.session) ?? asRecord(root.session);
  const sessionObj = nestedSession ?? dataObj ?? root;
  const rawMessages = extractRawSessionMessages(body);
  if (!Array.isArray(rawMessages)) {
    return [];
  }
  const usage = asUsageSummary(root.usage ?? dataObj?.usage ?? sessionObj.usage);
  const sessionProvenanceByIndex = parseSessionProvenanceIndex(
    asRecord(sessionObj.metadata)?.provenance ??
      asRecord(dataObj?.metadata)?.provenance ??
      asRecord(root.metadata)?.provenance,
  );
  const sessionMessageTimesByIndex = parseSessionMessageTimesIndex(
    asRecord(sessionObj.metadata)?.messageTimes ??
      asRecord(dataObj?.metadata)?.messageTimes ??
      asRecord(root.metadata)?.messageTimes,
  );

  // Mantle sessions persist OpenAI Responses items, a shape the Anthropic walk below cannot
  // read (it would drop SQL/tables and misfile narration as answers). The engine stamp is
  // authoritative; an invoke session never carries it, so the v1 path is untouched.
  const lastError = asRecord(asRecord(sessionObj.metadata)?.lastError ?? asRecord(root.metadata)?.lastError);
  const stampedMantle =
    asRecord(sessionObj.metadata)?.engine === 'mantle' || sessionObj.engine === 'mantle' || root.engine === 'mantle';
  if (stampedMantle) {
    const messages = mapResponsesItemsToMessages(
      rawMessages,
      sessionProvenanceByIndex,
      sessionMessageTimesByIndex,
      usage,
    );
    appendFailedRun(messages, lastError);
    return messages;
  }

  const result: Array<LeDataChatMessage> = [];
  for (let rawIndex = 0; rawIndex < rawMessages.length; rawIndex += 1) {
    const row = rawMessages[rawIndex];
    if (!row || typeof row !== 'object') {
      continue;
    }
    const r = row as Record<string, unknown>;
    const role = r.role === 'assistant' ? 'assistant' : 'user';
    const id = typeof r.id === 'string' ? r.id : newMessageId();
    const contentBlocks = asSessionContentBlocks(r.content);
    const createdAtFields = createdAtFromIndex(sessionMessageTimesByIndex, rawIndex);

    if (role === 'user') {
      // Handle new array format: content is an array of { type: 'image', source: {...} } and { type: 'text', text: '...' }
      if (Array.isArray(r.content)) {
        let text = '';
        const images: Array<LeDataImageAttachment> = [];
        for (const block of r.content) {
          if (!block || typeof block !== 'object') {
            continue;
          }
          const b = block as Record<string, unknown>;
          if (b.type === 'text' && typeof b.text === 'string') {
            text = b.text;
          } else if (b.type === 'image' && b.source && typeof b.source === 'object') {
            const src = b.source as Record<string, unknown>;
            if (typeof src.data === 'string' && typeof src.media_type === 'string') {
              images.push({
                data: src.data,
                mediaType: src.media_type as LeDataImageMediaType,
              });
            }
          }
        }
        if (text.trim() || images.length > 0) {
          result.push({
            id,
            role: 'user',
            text: text || undefined,
            images: images.length > 0 ? images : undefined,
            ...createdAtFields,
          });
          continue;
        }
      }

      const text = [r.text, r.content, r.question, r.message].find((x) => typeof x === 'string');
      if (text?.trim()) {
        result.push({ id, role: 'user', text, ...createdAtFields });
        continue;
      }
      if (contentBlocks?.length) {
        const contentText = extractTextFromBlocks(contentBlocks);
        const nonTextBlocks = extractNonTextBlocks(contentBlocks);

        if (contentText) {
          result.push({
            id,
            role: 'user',
            text: contentText,
            ...createdAtFields,
          });
        }

        if (nonTextBlocks.length > 0) {
          result.push({
            id: `${id}-results`,
            role: 'assistant',
            progress: null,
            blocks: nonTextBlocks,
            ...createdAtFields,
          });
        }
      }
      continue;
    }

    const answerMeta = extractAssistantAnswerMeta(r);
    const sessionProvenance = sessionProvenanceByIndex.get(rawIndex);
    if (sessionProvenance) {
      answerMeta.provenance ??= sessionProvenance;
      if (!answerMeta.traceId && sessionProvenance.traceId) {
        answerMeta.traceId = sessionProvenance.traceId;
      }
    }
    const blocks = asBlocks(r.blocks) ?? asBlocks(r.contentBlocks) ?? contentBlocks;
    if (blocks?.length) {
      result.push({ id, role: 'assistant', progress: null, blocks, ...answerMeta, ...createdAtFields });
      continue;
    }

    const text = [r.text, r.content, r.markdown, r.message].find((x) => typeof x === 'string');
    if (text?.trim()) {
      result.push({
        id,
        role: 'assistant',
        progress: null,
        blocks: [{ id: newBlockId(), type: 'text', payload: { markdown: text } }],
        ...answerMeta,
        ...createdAtFields,
      });
    }
  }
  if (usage) {
    // Attach session stats to the last assistant message.
    for (let i = result.length - 1; i >= 0; i -= 1) {
      if (result[i].role === 'assistant') {
        result[i] = { ...result[i], usage };
        break;
      }
    }
  }

  appendFailedRun(result, lastError);
  return result;
}

function appendFailedRun(messages: Array<LeDataChatMessage>, failure: Record<string, unknown> | null) {
  if (typeof failure?.message !== 'string') {
    return;
  }
  const block: LeDataContentBlock = { id: newBlockId(), type: 'error', payload: { message: failure.message } };
  const previous = messages[messages.length - 1];
  if (previous?.role === 'assistant') {
    messages[messages.length - 1] = { ...previous, blocks: [...(previous.blocks ?? []), block] };
  } else {
    messages.push({
      id: newMessageId(),
      role: 'assistant',
      progress: null,
      blocks: [block],
      ...(typeof failure.createdAt === 'string' ? { createdAt: failure.createdAt } : {}),
    });
  }
}

/**
 * Effective engine for a session response. The stamp is authoritative on every payload
 * (owner and shared); its absence means invoke, matching the backend contract.
 */
function detectSessionEngine(body: unknown, sessionSource: Record<string, unknown>): LeDataEngine {
  // Engine may arrive nested (`session.metadata.engine`), flat (`session.engine`, the mantle
  // getSession shape LeDataSessionMetadata declares), or top-level on the envelope.
  if (
    asRecord(sessionSource.metadata)?.engine === 'mantle' ||
    sessionSource.engine === 'mantle' ||
    asRecord(body)?.engine === 'mantle'
  ) {
    return 'mantle';
  }
  return 'invoke';
}

export function parseLeDataSessionResponse(body: unknown): LeDataSessionResponse {
  if (!body || typeof body !== 'object') {
    return { messages: [] };
  }

  const root = body as Record<string, unknown>;
  const data = asRecord(root.data);
  const sessionSource = asRecord(root.session) ?? asRecord(data?.session) ?? data ?? root;
  const usage = asUsageSummary(root.usage ?? data?.usage ?? sessionSource.usage);
  const session = asSessionMetadata(root.session ?? data?.session ?? data ?? root);
  const access = asSessionAccess(root.access ?? data?.access ?? sessionSource.access);
  const engine = detectSessionEngine(body, sessionSource);

  // Both engines reshow from their stored history: the invoke blob (Anthropic MessageParams)
  // or the mantle model-context ledger (Responses items), projected to chat rows by the same
  // code the live stream drives — so replay and live can never diverge.
  const messages = mapLeDataSessionResponseToMessages(body);

  const readOnly = (root.readOnly ?? data?.readOnly) === true;

  const response: LeDataSessionResponse = {
    messages,
    ...(usage ? { usage } : {}),
    ...(session ? { session: engine === 'mantle' ? { ...session, engine } : session } : {}),
    ...(access ? { access } : {}),
    engine,
    ...(readOnly ? { readOnly } : {}),
  };

  return response;
}
